import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  labelledBy?: string;
  /** `wide` stretches the panel on ≥sm screens for data-dense content (e.g. the screenshot-import
   *  review table); phones are full-width either way. */
  size?: 'default' | 'wide';
}

/**
 * A centered dialog over a dimmed backdrop. Closes on Escape or a backdrop CLICK — where "click"
 * requires the pointer to have gone DOWN on the backdrop too: a text-selection drag that starts
 * inside the panel and releases over the backdrop must never close the dialog (browsers fire
 * `click` on the mouseup element's common ancestor, so without the pointerdown check, selecting
 * text and letting go outside the panel closed it — user-reported, 2026-08-17).
 *
 * Portalled to `<body>` (REV30 fix, user-reported 2026-09-17): callers mount modals inside whatever
 * opened them — e.g. Characters' `AccountCard`, a `.card-hover` section. `:hover` follows the DOM
 * tree, so while the pointer is over the overlay the card is still "hovered" and gets its hover
 * `transform`; any transformed ancestor becomes the containing block for `position: fixed`, and the
 * overlay sized itself to the card instead of the viewport. A portal takes the overlay out of every
 * ancestor's layout for good, in every design. React events still bubble through the React tree, so
 * callers' handlers are unaffected.
 */
export function Modal({ title, onClose, children, size = 'default' }: ModalProps) {
  const pressStartedOnBackdrop = useRef(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4"
      onPointerDown={(event) => {
        pressStartedOnBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && pressStartedOnBackdrop.current) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        className={[
          'animate-toast-in max-h-[90vh] w-full overflow-y-auto rounded-t-2xl border border-border bg-bg-elevated p-5 shadow-2xl sm:rounded-2xl sm:p-6',
          size === 'wide' ? 'sm:max-w-4xl' : 'max-w-lg',
        ].join(' ')}
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="font-display text-lg font-semibold text-text">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex min-h-tap min-w-tap items-center justify-center rounded-lg text-xl text-text-faint transition-colors hover:bg-bg-elevated-hover hover:text-text"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

interface ConfirmDialogProps {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  danger = false,
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal title={title} onClose={onCancel}>
      <p className="text-sm text-text-muted">{message}</p>
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="min-h-tap rounded-lg px-4 text-sm font-semibold text-text-muted transition-colors hover:bg-bg-elevated-hover"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={pending}
          onClick={onConfirm}
          className={[
            'min-h-tap rounded-lg px-4 text-sm font-semibold shadow-sm transition-colors disabled:opacity-50',
            danger
              ? 'bg-danger text-white hover:bg-danger/85'
              : 'bg-accent text-accent-contrast hover:bg-accent-strong',
          ].join(' ')}
        >
          {pending ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
